import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Alert, 
  Card, 
  CardContent, 
  List, 
  ListItem, 
  ListItemText, 
  Divider 
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import NavBar from '../components/common/NavBar';
import StripePaymentForm from '../components/payments/StripePaymentForm';
import DevPaymentForm from '../components/payments/DevPaymentForm';
import api from '../services/api';
import { REACT_APP_STRIPE_PUBLISHABLE_KEY, STRIPE_CONFIG } from '../config/Config';

// Load Stripe (will use test key in development)
const stripePromise = loadStripe(REACT_APP_STRIPE_PUBLISHABLE_KEY);

const AddOnPaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  const [loading, setLoading] = useState(true);
  const [stripeConfig, setStripeConfig] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    // Get cart items from location state or load from session
    const items = location.state?.cartItems || [];
    const total = location.state?.totalAmount || 0;

    if (!items.length) {
      enqueueSnackbar('No items in cart. Please add items first.', { variant: 'error' });
      navigate('/profile');
      return;
    }

    setCartItems(items);
    setTotalAmount(total);

    // Get Stripe configuration
    fetchStripeConfig();
  }, [location, navigate, enqueueSnackbar]);

  const fetchStripeConfig = async () => {
    try {
      const response = await api.get(STRIPE_CONFIG);
      setStripeConfig(response.data);
    } catch (err) {
      console.error('Error fetching Stripe config:', err);
      enqueueSnackbar('Failed to load payment configuration', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (paymentData) => {
    enqueueSnackbar('Payment successful! Your add-ons have been added to your account.', { variant: 'success' });
    
    // Redirect back to profile page
    navigate('/profile', {
      replace: true,
      state: {
        message: 'Add-ons purchased successfully!',
      }
    });
  };

  const handlePaymentCancel = () => {
    enqueueSnackbar('Payment cancelled. You can complete payment later.', { variant: 'info' });
    
    // Redirect back to profile page
    navigate('/profile', { replace: true });
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'background.default' }}>
        <NavBar />
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (!cartItems.length) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'background.default' }}>
        <NavBar />
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Typography>No items in cart</Typography>
        </Box>
      </Box>
    );
  }

  const isDevelopment = !stripeConfig?.isProduction;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'background.default' }}>
      <NavBar />
      
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <Card sx={{ maxWidth: 600, width: '100%', mx: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" gutterBottom align="center">
              Complete Your Add-On Purchase
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              {isDevelopment 
                ? 'Development Mode: This is a test payment. No real charges will be made.'
                : 'Please complete your payment to add these items to your account.'
              }
            </Alert>

            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Order Summary
              </Typography>
              <List dense>
                {cartItems.map((item, index) => (
                  <React.Fragment key={index}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={item.label}
                        secondary={`Quantity: ${item.quantity} × $${item.unit_price} = $${item.total_price}`}
                      />
                    </ListItem>
                    {index < cartItems.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" align="right">
                Total: ${totalAmount.toFixed(2)}
              </Typography>
            </Box>

            {isDevelopment ? (
              <DevPaymentForm
                planCode="addon_purchase"
                planName="Add-On Purchase"
                planPrice={totalAmount}
                changeReason="Add-on purchase from profile"
                cartItems={cartItems}
                onSuccess={handlePaymentSuccess}
                onCancel={handlePaymentCancel}
              />
            ) : (
              <Elements stripe={stripePromise}>
                <StripePaymentForm
                  planCode="addon_purchase"
                  planName="Add-On Purchase"
                  planPrice={totalAmount}
                  changeReason="Add-on purchase from profile"
                  cartItems={cartItems}
                  onSuccess={handlePaymentSuccess}
                  onCancel={handlePaymentCancel}
                />
              </Elements>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default AddOnPaymentPage; 